// Shipping rate calculator for the product page.
// Reads CEP, infers Brazilian state from prefix, and queries Shopify's
// shipping rates API by temporarily replacing the cart with the product
// being viewed, then restoring the original cart contents.

// Pix discount applied automatically at checkout. We mirror it here so the
// free-shipping eligibility check uses the same effective price the customer
// actually pays. Set to 0 if your product.price in Liquid is already the
// post-Pix price.
// IMPORTANT: keep this in sync with the active checkout discount.
const PIX_DISCOUNT_PERCENT = 5;

// Free-shipping rules — these mirror the Octolize Shipping Discounts app
// configured in the Shopify admin. Octolize applies the discount at
// checkout; we replicate the same conditions here so the calculator
// preview matches what the customer will see.
// IMPORTANT: keep this in sync with the Octolize app config.
const FREE_SHIPPING_RULES = {
  minCartTotal: 299,
  states: ["São Paulo"],
  cities: ["Rio de Janeiro", "Seropédica", "Niterói", "São Gonçalo", "Itaboraí", "Tanguá", "Maricá", "Duque de Caxias", "São João de Meriti", "Petrópolis", "Magé", "Guapimirim", "Nova Iguaçu", "Belford Roxo", "Queimados", "Japeri", "Nilópolis", "Mesquita", "Vitória", "Vila Velha", "Viana", "Cariacica", "Serra", "Fundão", "Guarapari", "Belo Horizonte", "Contagem", "Ibirité", "Sarzedo", "Mário Campos", "Brumadinho", "Betim", "Igarapé", "São Joaquim de Bicas", "Santa Luzia", "Vespasiano", "Lagoa Santa", "Pedro Leopoldo", "São José da Lapa", "Confins", "Ribeirão das Neves", "Taquaraçu de Minas", "Nova Lima", "Rio Acima", "Raposos", "Sabará", "Caeté", "Nova União", "Itabirito", "Mateus Leme", "Juatuba", "Florestal", "Sete Lagoas", "Matozinhos", "Capim Branco", "Funilândia", "Prudente de Morais", "Jaboticatubas", "Barão de Cocais", "Curitiba", "São José dos Pinhais", "Piraquara", "Pinhais", "Colombo", "Quatro Barras", "Campina Grande do Sul", "Bocaiúva do Sul", "Almirante Tamandaré", "Campo Magro", "Rio Branco do Sul", "Itaperuçu", "Campo Largo", "Balsa Nova", "Araucária", "Contenda", "Mandirituba", "Fazenda Rio Grande", "Florianópolis", "São José", "São Pedro de Alcântara", "Palhoça", "Santo Amaro da Imperatriz", "Águas Mornas", "Biguaçu", "Antônio Carlos", "Governador Celso Ramos", "Tijucas", "Canelinha", "São João Batista", "Major Gercino", "Nova Trento", "Angelina", "Rancho Queimado", "São Bonifácio", "Paulo Lopes", "Garopaba", "Porto Alegre", "Canoas", "Nova Santa Rita", "Montenegro", "Guaíba", "Eldorado do Sul", "São Leopoldo", "Portão", "Sapucaia do Sul", "Esteio", "Novo Hamburgo", "Estância Velha", "Campo Bom", "Sapiranga", "Ivoti", "Dois Irmãos", "Gravataí", "Glorinha", "Viamão", "Tramandaí", "Osório", "Capão da Canoa", "Alvorada", "Cachoeirinha", "Capela de Santana", "Triunfo", "São Jerônimo", "Arroio dos Ratos", "Charqueadas"],
};

// Pin icon for the resolved-address line under the CEP field. Inline rather
// than a snippet because the line is built in JS after the lookup resolves.
const CEP_PIN_ICON =
  '<svg width="12" height="14" viewBox="0 0 12 14" aria-hidden="true" focusable="false">' +
  '<path fill="currentColor" d="M6 .8a4.7 4.7 0 0 0-4.7 4.7c0 3.4 4.2 7.5 4.4 7.7a.4.4 0 0 0 .6 0c.2-.2 4.4-4.3 4.4-7.7A4.7 4.7 0 0 0 6 .8Zm0 6.5a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6Z"></path>' +
  '</svg>';

// Full address for a CEP. Three distinct outcomes, and callers need to tell
// them apart: an address object, `null` when the providers agree the CEP does
// not exist, and `undefined` when the lookup itself failed (offline, timeout)
// and nothing can be concluded.
async function getAddressFromCep(cep) {
  // Session cache: only definitive answers, so a transient failure isn't
  // pinned for the rest of the session.
  const cacheKey = `cepAddr:${cep}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch (_) {
    // sessionStorage may be unavailable (private mode, blocked) — ignore.
  }

  const fetchWithTimeout = async (url, ms) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (res.status === 404) return { missing: true };
      return res.ok ? await res.json() : null;
    } catch (_) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  // Primary: ViaCEP. Fallback: BrasilAPI if ViaCEP errors, times out, or
  // returns the {erro: true} shape for an unknown CEP — BrasilAPI aggregates
  // several sources and occasionally resolves a CEP ViaCEP doesn't have.
  let address;
  const viaCep = await fetchWithTimeout(`https://viacep.com.br/ws/${cep}/json/`, 2500);
  if (viaCep && !viaCep.erro && !viaCep.missing && viaCep.localidade) {
    address = {
      street: viaCep.logradouro || "",
      city: viaCep.localidade,
      uf: viaCep.uf || "",
    };
  } else {
    const brasilApi = await fetchWithTimeout(`https://brasilapi.com.br/api/cep/v1/${cep}`, 2500);
    if (brasilApi && brasilApi.city) {
      address = {
        street: brasilApi.street || "",
        city: brasilApi.city,
        uf: brasilApi.state || "",
      };
    } else if ((viaCep && (viaCep.erro || viaCep.missing)) || (brasilApi && brasilApi.missing)) {
      // At least one provider answered, and the answer was "no such CEP".
      address = null;
    } else {
      return undefined; // lookup failed; say nothing
    }
  }

  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(address));
  } catch (_) {
    // ignore quota / availability issues
  }
  return address;
}

async function getCityFromCep(cep) {
  const address = await getAddressFromCep(cep);
  return address && address.city ? address.city : null;
}

// "Rua das Flores, São Paulo - SP", dropping whichever parts the CEP doesn't
// carry — CEPs that cover a whole município have no logradouro.
function formatCepAddress(address) {
  if (!address) return "";
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.uf ? `${address.city} - ${address.uf}` : address.city);
  else if (address.uf) parts.push(address.uf);
  return parts.join(", ");
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
  });
}

// Renders the confirmation line under the CEP field: the pin icon plus the
// address, or a "not found" note so a typo doesn't read as a shipping failure.
function renderCepAddress(state, address) {
  const el = document.querySelector("[data-cep-address]");
  if (!el) return;

  if (state === "address" && address) {
    el.className = "shipping-cep-address";
    el.innerHTML = `${CEP_PIN_ICON}<span>${escapeHtml(formatCepAddress(address))}</span>`;
    el.hidden = false;
    return;
  }
  if (state === "notfound") {
    el.className = "shipping-cep-address is-notfound";
    el.innerHTML = `${CEP_PIN_ICON}<span>CEP não encontrado</span>`;
    el.hidden = false;
    return;
  }
  el.innerHTML = "";
  el.hidden = true;
}

// Fills the field with the last CEP the customer used anywhere on the site
// (the cart drawer and the cart page write the same key), falling back to
// their saved address. Only the field and the address line are filled — no
// rates are fetched, because quoting swaps the cart and that must stay an
// explicit action.
function prefillCep() {
  const input = document.getElementById("cep-calc");
  if (!input || input.value.trim() !== "") return;

  let cep = "";
  try {
    cep = (localStorage.getItem("vinsel:cep") || "").replace(/\D/g, "");
  } catch (_) {
    // private mode / disabled storage
  }
  if (cep.length !== 8) {
    cep = (input.getAttribute("data-customer-zip") || "").replace(/\D/g, "");
  }
  if (cep.length !== 8) return;

  input.value = `${cep.slice(0, 5)}-${cep.slice(5)}`;
  showCepAddress(cep);
}

// Bumped per lookup so a slow response for an old CEP can't overwrite the
// line after the customer has already typed a different one.
let cepAddressGeneration = 0;

async function showCepAddress(cep) {
  const generation = ++cepAddressGeneration;
  if (!/^\d{8}$/.test(cep)) {
    renderCepAddress("hidden");
    return;
  }
  const address = await getAddressFromCep(cep);
  if (generation !== cepAddressGeneration) return;
  if (address === undefined) renderCepAddress("hidden");
  else if (address === null) renderCepAddress("notfound");
  else renderCepAddress("address", address);
}

function getSkuDataToShippingRates() {
  var e = $("#cep-calc")[0].value,
    t = $("#calculate-shipping").data("skuid"),
    a = $(".right-side .quantity_shelf").val(),
    n = $("#calculate-shipping").data("value"),
    i = $("#calculate-shipping").data("weight");

    console.log(
      e.replace("-", ""),
      i / 1e3,
      n.replace(",", "."),
      t,
      a,
    );

  return {
    data: {
      cep: e.replace("-", ""),
      weight: i / 1e3,
      Itemprice: n.replace(",", "."),
      skuId: t,
      qtd: a,
    },
    pattern: /^\d{5}-\d{3}$/,
    cepValue: e,
  };
}

function getStateFromZipPrefix(cep) {
    // Priority Map: 3-digit prefixes for ambiguous regions, 2-digit for the rest.
    const zipPrefixToState = {
        // 3-digit lookups for critical zones (Example for '69' and other regions that split):
        "689": "Amapá",          // 689xx: Amapá (AP)
        "699": "Acre",           // 699xx: Acre (AC)
        "693": "Roraima",        // 693xx: Roraima (RR)
        // Note: 690-692 and 694-698 are Amazonas (covered by 69)

        // Standard 2-digit lookups (Ensures every prefix is covered):
        "0": "São Paulo", "01": "São Paulo", "02": "São Paulo", "03": "São Paulo", "04": "São Paulo",
        "05": "São Paulo", "06": "São Paulo", "07": "São Paulo", "08": "São Paulo", "09": "São Paulo",
        "1": "São Paulo", "10": "São Paulo", "11": "São Paulo", "12": "São Paulo", "13": "São Paulo",
        "14": "São Paulo", "15": "São Paulo", "16": "São Paulo", "17": "São Paulo", "18": "São Paulo", "19": "São Paulo",
        "20": "Rio de Janeiro", "21": "Rio de Janeiro", "22": "Rio de Janeiro", "23": "Rio de Janeiro",
        "24": "Rio de Janeiro", "25": "Rio de Janeiro", "26": "Rio de Janeiro", "27": "Rio de Janeiro",
        "28": "Rio de Janeiro",
        "29": "Espírito Santo",
        "30": "Minas Gerais", "31": "Minas Gerais", "32": "Minas Gerais", "33": "Minas Gerais",
        "34": "Minas Gerais", "35": "Minas Gerais", "36": "Minas Gerais", "37": "Minas Gerais",
        "38": "Minas Gerais", "39": "Minas Gerais",
        "40": "Bahia", "41": "Bahia", "42": "Bahia", "43": "Bahia", "44": "Bahia", "45": "Bahia",
        "46": "Bahia", "47": "Bahia", "48": "Bahia",
        "49": "Sergipe",
        "50": "Pernambuco", "51": "Pernambuco", "52": "Pernambuco", "53": "Pernambuco", "54": "Pernambuco",
        "55": "Pernambuco", "56": "Pernambuco",
        "57": "Alagoas",
        "58": "Paraíba",
        "59": "Rio Grande do Norte",
        "60": "Ceará", "61": "Ceará", "62": "Ceará", "63": "Ceará",
        "64": "Piauí",
        "65": "Maranhão",
        "66": "Pará", "67": "Mato Grosso do Sul", "68": "Pará",
        "69": "Amazonas", // Amazonas is the default for 69xx that isn't 693 or 699
        "70": "Distrito Federal", "71": "Distrito Federal", "72": "Distrito Federal", "73": "Distrito Federal",
        "74": "Goiás", "75": "Goiás", "76": "Goiás",
        "77": "Tocantins",
        "78": "Mato Grosso",
        "79": "Mato Grosso do Sul",
        "80": "Paraná", "81": "Paraná", "82": "Paraná", "83": "Paraná", "84": "Paraná", "85": "Paraná",
        "86": "Paraná", "87": "Paraná",
        "88": "Santa Catarina",
        "89": "Santa Catarina",
        "9": "Rio Grande do Sul", "90": "Rio Grande do Sul", "91": "Rio Grande do Sul", "92": "Rio Grande do Sul",
        "93": "Rio Grande do Sul", "94": "Rio Grande do Sul", "95": "Rio Grande do Sul", "96": "Rio Grande do Sul",
        "97": "Rio Grande do Sul", "98": "Rio Grande do Sul", "99": "Rio Grande do Sul",
    };

    const prefix3 = cep.substring(0, 3);
    const prefix2 = cep.substring(0, 2);

    // 1. Check for the specific 3-digit prefix first
    let provinceName = zipPrefixToState[prefix3];

    // 2. If no match, fall back to the 2-digit prefix
    if (!provinceName) {
        provinceName = zipPrefixToState[prefix2];
    }

    return provinceName || null;
}

async function calcShipping(e, t, a) {
  // e is the CEP (zip code), t is the variant id, a is the quantity

  const provinceName = getStateFromZipPrefix(e);

  if (!provinceName) {
    // Handle error: CEP prefix not recognized or invalid.
    $(".toast").addClass("active");
    $(".spinner").hide();
    document.querySelector("#calculate-shipping").removeAttribute("disabled");
    $("#calculate-shipping").css({ cursor: "pointer" });
    return; // Stop execution
  }

  // Octolize eligibility is computed using only this product's line subtotal
  // (price × quantity), with the Pix discount applied to mirror what
  // checkout sees. The product-page calculator intentionally ignores items
  // already in the cart — the user is asking about shipping for THIS
  // product, not their cart at large.
  let inZone = false;
  let eligible = false;
  try {
    // pt-BR currency format from money_without_currency is "1.299,90" —
    // strip thousands dots first, then swap the decimal comma for a dot.
    const raw = String($("#calculate-shipping").data("value"));
    const productPrice = parseFloat(raw.replace(/\./g, "").replace(",", ".")) || 0;
    const productPricePix = productPrice * (1 - PIX_DISCOUNT_PERCENT / 100);
    const lineTotal = productPricePix * Number(a);

    // Only fetch the city when the state alone doesn't trigger the rule.
    let city = null;
    if (!FREE_SHIPPING_RULES.states.includes(provinceName)) {
      city = await getCityFromCep(e);
    }

    inZone =
      FREE_SHIPPING_RULES.states.includes(provinceName) ||
      (!!city && FREE_SHIPPING_RULES.cities.includes(city));
    eligible = inZone && lineTotal >= FREE_SHIPPING_RULES.minCartTotal;
  } catch (err) {
    console.error("[shipping] eligibility check failed", err);
  }
  $("#shipping-area").data("octolize", { eligible, inZone });

  await createShippingRates(
    {
      zip: e,
      country: "Brazil",
      province: provinceName,
    },
    t,
    a
  );
}

// ---------------------------------------------------------------------------
// Cart swap + rate lookup
//
// Quoting a single product requires the cart to hold only that product, so we
// snapshot the customer's cart, swap it out, quote, and put it back. Every
// step is awaited: /cart/shipping_rates.json always describes the cart as it
// stands at the moment it answers, so restoring before the rates arrive hands
// back rates for the customer's real basket instead of the product on screen.
// ---------------------------------------------------------------------------

// Bumped on every calculation. A lookup that finishes after a newer one has
// started throws its result away instead of overwriting fresher rows.
let calcGeneration = 0;

// Cart swaps are serialised. Two overlapping swaps would let the second one
// snapshot the first one's temporary single-product cart and then "restore"
// that as the customer's cart.
let cartSwapChain = Promise.resolve();

// Captured at bind time so the button goes back to the label the section
// rendered ("Consultar") rather than a hard-coded string.
let calcButtonLabel = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function cartPost(url, body) {
  const options = {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
  };
  if (body) options.body = JSON.stringify(body);
  const res = await fetch(url, options);
  if (!res.ok) {
    // Shopify explains cart rejections in the body — most usefully the 422 from
    // /cart/add.js when the requested quantity exceeds stock. Carry it up so the
    // customer is told what actually went wrong.
    let detail = "";
    try {
      const payload = await res.json();
      detail = payload.description || payload.message || "";
    } catch (_) {
      // non-JSON error body; the status alone will have to do
    }
    const error = new Error(`${url} responded ${res.status}${detail ? ": " + detail : ""}`);
    error.status = res.status;
    error.detail = detail;
    throw error;
  }
  return res.status === 204 ? null : res.json();
}

// Line items in the shape /cart/add.js accepts, so the cart can be rebuilt
// exactly as the customer left it.
async function snapshotCart() {
  const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`/cart.js responded ${res.status}`);
  const cart = await res.json();
  return cart.items.map((item) => {
    const line = { id: item.id, quantity: item.quantity };
    if (item.properties && Object.keys(item.properties).length > 0) {
      line.properties = item.properties;
    }
    const allocation = item.selling_plan_allocation;
    if (allocation && allocation.selling_plan) {
      line.selling_plan = allocation.selling_plan.id;
    }
    return line;
  });
}

async function restoreCart(items) {
  await cartPost("/cart/clear.js");
  if (items.length > 0) await cartPost("/cart/add.js", { items });
  getAndUpdateMinicartInFront();
  updateItemCountCart();
}

// cart-drawer-shipping.js quotes the same cart and re-runs on every drawer
// re-render. Left alone it fires its own prepare_shipping_rates while we are
// still polling, and our poll reads back its rates for the restored cart.
function pauseCartDrawerShipping() {
  window.__vinselCartDrawerShippingPaused = true;
  // Bumped per swap so a drawer lookup that was already in flight can tell its
  // result describes a cart that no longer exists.
  window.__vinselCartSwapSeq = (window.__vinselCartSwapSeq || 0) + 1;
}

function resumeCartDrawerShipping() {
  window.__vinselCartDrawerShippingPaused = false;
  if (typeof window.__vinselCartDrawerShippingRefresh === "function") {
    window.__vinselCartDrawerShippingRefresh();
  }
}

function createShippingRates(shippingData, id, qtd) {
  const generation = ++calcGeneration;
  const run = cartSwapChain
    .catch(() => {})
    .then(() => runShippingRateSwap(shippingData, id, qtd, generation));
  cartSwapChain = run.catch(() => {});
  return run;
}

async function runShippingRateSwap(shippingData, id, qtd, generation) {
  // A newer request queued up while this one was waiting its turn.
  if (generation !== calcGeneration) return;

  let snapshot = null;
  pauseCartDrawerShipping();

  try {
    snapshot = await snapshotCart();
    await cartPost("/cart/clear.js");
    await cartPost("/cart/add.js", {
      items: [{ id: parseInt(id, 10), quantity: parseInt(qtd, 10) }],
    });

    const rates = await getShippingRates(shippingData);
    if (generation === calcGeneration) createShippingTable(rates);
  } catch (err) {
    console.error("[shipping] rate lookup failed", err);
    // A cart operation that failed is not the same as a CEP the carriers don't
    // serve. Reporting both as "Nenhuma opção de frete disponível" hid a
    // quantity-vs-stock rejection behind a shipping message.
    let message = "Não foi possível calcular o frete agora. Tente novamente.";
    if (err && err.status === 422 && err.detail) message = err.detail;
    if (generation === calcGeneration) createShippingTable([], message);
  } finally {
    if (snapshot) {
      try {
        await restoreCart(snapshot);
      } catch (err) {
        console.error("[shipping] cart restore failed", err);
      }
    }
    resumeCartDrawerShipping();
  }
}

async function getShippingRates(shippingData) {
  if (!shippingData) return [];

  // URLSearchParams escapes the brackets and the accented province names
  // ("São Paulo", "Espírito Santo") that used to go into the query raw.
  const params = new URLSearchParams();
  params.set("shipping_address[zip]", shippingData.zip);
  params.set("shipping_address[country]", shippingData.country);
  params.set("shipping_address[province]", shippingData.province);

  $("#calculate-shipping").html("Consultando...");

  await fetch("/cart/prepare_shipping_rates.json", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });

  const url = `/cart/shipping_rates.json?${params.toString()}`;

  // Shopify computes rates asynchronously and answers 422 while the job is
  // still running. With a carrier service in the mix (Cota Frete calling the
  // transportadoras) it can also answer 200 with an empty list before the
  // carrier has replied, so an empty list only means "nothing ships there"
  // once the whole ladder has been walked. Giving empty results a short
  // budget is what made a recalculation report "Nenhuma opção" for a CEP that
  // had quoted fine seconds earlier.
  const delays = [200, 350, 500, 750, 1000, 1300, 1600, 1900, 2200, 2500];
  let attempt = 0;

  while (true) {
    const res = await fetch(url, { headers: { Accept: "application/json" } });

    if (res.status === 422) {
      if (attempt >= delays.length) {
        console.warn("[shipping] gave up waiting for rates (422)", shippingData.zip);
        return [];
      }
      await wait(delays[attempt++]);
      continue;
    }
    if (!res.ok) {
      console.error("[shipping] shipping_rates.json responded", res.status);
      return [];
    }

    const json = await res.json();
    const rates = (json && json.shipping_rates) || [];
    if (rates.length === 0 && attempt < delays.length) {
      await wait(delays[attempt++]);
      continue;
    }

    console.log("[shipping] rates for", shippingData.zip, rates);
    return rates;
  }
}

function pulseShippingArea() {
  const area = document.getElementById("shipping-area");
  if (!area) return;
  area.style.animation = "none";
  void area.offsetHeight;
  area.style.animation = "";
}

function createShippingTable(rates, emptyMessage) {
  $("#shipping-area").removeClass("is-loading").empty();
  $(".spinner").hide();
  document.querySelector("#calculate-shipping").removeAttribute("disabled");
  $("#calculate-shipping").css({ cursor: "pointer" });
  $("#calculate-shipping").html(calcButtonLabel || "Consultar");

  if (!rates || rates.length === 0) {
    const notice =
      emptyMessage || "Nenhuma opção de frete disponível para o CEP informado.";
    $("#shipping-area").append(
      `<p class="no-shipping-message">${escapeHtml(notice)}</p>`
    );
    pulseShippingArea();
    return;
  }

  // If the Octolize free-shipping rule applies, mark the cheapest paid rate
  // as discounted. Octolize discounts the cheapest method only.
  const oct = $("#shipping-area").data("octolize") || {};
  const eligible = oct.eligible === true;
  let discountedIdx = -1;
  if (eligible) {
    let cheapest = Infinity;
    rates.forEach((rate, idx) => {
      const price = parseFloat(rate.price);
      if (price > 0 && price < cheapest) {
        cheapest = price;
        discountedIdx = idx;
      }
    });
  }

  const table = $('<div class="shipping-table"></div>');
  rates.forEach((rate, idx) => {
    const { name, price } = rate;
    const eta = formatEta(rate);
    let priceHtml;
    let lineClass = "shipping-line";

    if (idx === discountedIdx) {
      lineClass += " is-free";
      const original = "R$ " + price.replace(".", ",");
      priceHtml = `<span class="price-original">${original}</span><span class="price-free">Frete grátis</span>`;
    } else if (parseFloat(price) <= 0) {
      priceHtml = "<strong>Frete Grátis</strong>";
    } else {
      priceHtml = "R$ " + price.replace(".", ",");
    }

    table.append($(`
      <div class="${lineClass}">
          <p>${name}</p>
          <div>
              <p>${eta}</p>
              <p>${priceHtml}</p>
          </div>
      </div>
    `));
  });
  $("#shipping-area").html(table);

  // "You're in a free-shipping zone but haven't hit the minimum yet" hint.
  if (oct.inZone && !oct.eligible) {
    const minDisplay = FREE_SHIPPING_RULES.minCartTotal.toLocaleString("pt-BR");
    $("#shipping-area").append(
      `<div class="octolize-notice">Você está em área de frete grátis. Atinja o mínimo de R$ ${minDisplay} em produtos para obter o desconto!</div>`
    );
  }

  pulseShippingArea();
}

// Shown when no source carries a prazo. A quote without one isn't something
// Brazilian customers expect, so the row says so plainly rather than going
// blank — and never "0 dias úteis", which is what the old code printed for
// every quote because `new Date(null)` is the 1970 epoch.
const ETA_UNKNOWN_TEXT = "Prazo a confirmar";

// Prefer Shopify's structured fields: delivery_days (already a business-day
// count), then the end of delivery_range, then delivery_date. Carrier apps
// that leave all three empty — Cota Frete among them — tend to put the prazo
// in prose instead, so the rate's text fields are parsed as a last resort.
function formatEta(rate) {
  const days = estimateBusinessDays(rate);
  if (days === null) return ETA_UNKNOWN_TEXT;
  if (days <= 1) return "Em até 1 dia útil";
  return `Em até ${days} dias úteis`;
}

function estimateBusinessDays(rate) {
  if (Array.isArray(rate.delivery_days) && rate.delivery_days.length > 0) {
    const days = rate.delivery_days.map(Number).filter((d) => Number.isFinite(d));
    if (days.length > 0) return Math.max(...days);
  }

  const range = rate.delivery_range;
  const dateStr =
    (Array.isArray(range) && range.length > 0 ? range[range.length - 1] : null) ||
    rate.delivery_date;
  if (dateStr) {
    const target = parseLocalDate(dateStr);
    if (target) {
      const cursor = new Date();
      cursor.setHours(0, 0, 0, 0);
      let businessDays = 0;
      while (cursor < target) {
        cursor.setDate(cursor.getDate() + 1);
        const weekday = cursor.getDay();
        if (weekday !== 0 && weekday !== 6) businessDays++;
      }
      return businessDays;
    }
  }

  // Which field the app uses varies, so try each one that could hold prose.
  const texts = [rate.description, rate.presentment_name, rate.name];
  for (const text of texts) {
    const days = daysFromText(text);
    if (days !== null) return days;
  }
  return null;
}

// Pulls a business-day count out of free text: "5 a 8 dias úteis" → 8,
// "entrega em 7 dias úteis" → 7, "até 24 horas" → 1. Ranges resolve to the
// upper bound, so the number on screen is the one the customer can hold you to.
function daysFromText(text) {
  if (!text) return null;
  const s = String(text).toLowerCase();

  const range = /(\d+)\s*(?:a|at[ée]|-|–|—|e)\s*(\d+)\s*dias?/.exec(s);
  if (range) return Math.max(Number(range[1]), Number(range[2]));

  const single = /(\d+)\s*dias?\b/.exec(s);
  if (single) return Number(single[1]);

  const hours = /(\d+)\s*horas?\b/.exec(s);
  if (hours) return Number(hours[1]) <= 24 ? 1 : Math.ceil(Number(hours[1]) / 24);

  return null;
}

// "2026-10-05" through the Date constructor is UTC midnight, which is still
// the previous day in BRT and costs a day off the count. Build it locally.
function parseLocalDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

$(function () {
  // Bind the Consultar click once the calculator markup is on the page.
  const intervalCalc = setInterval(() => {
    if ($("#calculate-shipping").length > 0) {
      clearInterval(intervalCalc);
      calcButtonLabel = $("#calculate-shipping").text().trim();
      prefillCep();

      // Resolve the address as soon as a full CEP is on screen, so the
      // customer can confirm the destination before asking for rates.
      const debouncedCepAddress = _.debounce(function () {
        showCepAddress($("#cep-calc").val().replace(/\D/g, ""));
      }, 400);
      $("#cep-calc").on("input", debouncedCepAddress);

      // Submit on Enter from inside the CEP field, with a brief pressed
      // visual on the button so the keyboard interaction feels like a click.
      $("#cep-calc").on("keydown", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          const $btn = $("#calculate-shipping");
          $btn.addClass("is-pressed");
          setTimeout(() => $btn.removeClass("is-pressed"), 150);
          $btn.trigger("click");
        }
      });

      $("#calculate-shipping").on("click", async function (e) {
        $(".toast").removeClass("active");
        var {
          data: { cep: t, qtd: a, skuId: n },
          pattern: i,
          cepValue: r,
        } = getSkuDataToShippingRates();
        if (!i.test(r)) {
          $(".toast").addClass("active");
          return;
        }
        // Persist the digits-only CEP so the cart drawer can pick it up on
        // any page without re-asking the customer.
        try { localStorage.setItem('vinsel:cep', t); } catch (_) {}
        showCepAddress(t);

        // Every click recalculates. The old same-CEP short circuit meant a
        // failed lookup stayed on screen forever, because the retry click was
        // exactly the one being skipped.

        // If a previous result is on screen, fade it in place; otherwise
        // fall back to the first-time global spinner.
        if ($("#shipping-area").children().length > 0) {
          $("#shipping-area").addClass("is-loading");
        } else {
          $(".spinner").show();
        }
        $("#calculate-shipping").attr("disabled", true);
        $("#calculate-shipping").css({ cursor: "not-allowed" });
        await calcShipping(t, n, a);
      });
    }
  }, 1000);

  // Recalculate when the user changes quantity, but only if a result is already shown.
  // Keep the existing rates visible at reduced opacity with a spinner overlay
  // so the layout doesn't jump while the new rates are being fetched.
  const debouncedCalcShipping = _.debounce(async function () {
    if ($("#shipping-area *").length > 1) {
      const {
        data: { cep, qtd, skuId },
      } = getSkuDataToShippingRates();
      $("#shipping-area").addClass("is-loading");
      await calcShipping(cep, skuId, qtd);
    }
  }, 500);
  $(".quantity-buttons").on("click", debouncedCalcShipping);
});
